import { useState, useEffect, useMemo } from "preact/hooks"
import { api_fetch } from "../../../api/client"
import { exactCurrencyFormatter } from "../../../utils"
import { Loader2, ChevronRight, ChevronDown, FlaskConical } from "lucide-react"
import { Button } from "../../../components/ui/button"
import { Checkbox } from "../../../components/ui/checkbox"

// ── Types (mirror server/src/promotional_context/trade_rate_handler.rs) ───────

export interface TradeRatePromotion {
  id: string
  name: string
  remote_promo_id: string | null
  customer: string | null
  retailer: string | null
  promotion_type: string | null
  stage: string
  party_key: string
  planned_amount: number
  latest_estimate_amount: number
  actual_amount: number
}

export interface TradeRatePartyRow {
  party_key: string
  party_label: string
  planned_amount: number
  latest_estimate_amount: number
  matched_spend: number
  unplanned_spend: number
  total_spend: number
  revenue: number
  promotion_count: number
  planned_trade_rate_pct: number | null
  estimate_trade_rate_pct: number | null
  actual_trade_rate_pct: number | null
}

export interface TradeRateSummaryResponse {
  rows: TradeRatePartyRow[]
  totals: TradeRatePartyRow
  promotions: TradeRatePromotion[]
}

// ── What-if math (pure; unit-tested in trade_rate_summary_view.test.ts) ───────

/**
 * Recompute a party row's planned/estimate spend and rates with a set of promotions excluded.
 * Cents-based math to avoid float drift when summing many promotions.
 */
export function apply_exclusions(
  row: TradeRatePartyRow,
  promotions: TradeRatePromotion[],
  excluded: Set<string>,
): TradeRatePartyRow {
  let removed_planned_cents = 0
  let removed_estimate_cents = 0
  for (const promo of promotions) {
    if (promo.party_key !== row.party_key || !excluded.has(promo.id)) continue
    removed_planned_cents += Math.round(promo.planned_amount * 100)
    removed_estimate_cents += Math.round(promo.latest_estimate_amount * 100)
  }
  if (removed_planned_cents === 0 && removed_estimate_cents === 0) return row
  const planned_amount = (Math.round(row.planned_amount * 100) - removed_planned_cents) / 100
  const latest_estimate_amount = (Math.round(row.latest_estimate_amount * 100) - removed_estimate_cents) / 100
  return {
    ...row,
    planned_amount,
    latest_estimate_amount,
    planned_trade_rate_pct: row.revenue ? (planned_amount / row.revenue) * 100 : null,
    estimate_trade_rate_pct: row.revenue ? (latest_estimate_amount / row.revenue) * 100 : null,
  }
}

/** Recompute the totals row across already-adjusted party rows. */
export function recompute_totals(rows: TradeRatePartyRow[], base: TradeRatePartyRow): TradeRatePartyRow {
  const planned_cents = rows.reduce((s, r) => s + Math.round(r.planned_amount * 100), 0)
  const estimate_cents = rows.reduce((s, r) => s + Math.round(r.latest_estimate_amount * 100), 0)
  const planned_amount = planned_cents / 100
  const latest_estimate_amount = estimate_cents / 100
  return {
    ...base,
    planned_amount,
    latest_estimate_amount,
    planned_trade_rate_pct: base.revenue ? (planned_amount / base.revenue) * 100 : null,
    estimate_trade_rate_pct: base.revenue ? (latest_estimate_amount / base.revenue) * 100 : null,
  }
}

// ── Formatting helpers ────────────────────────────────────────────────────────

function pct(value: number | null): string {
  return value === null ? "—" : `${value.toFixed(1)}%`
}

function money(value: number): string {
  return exactCurrencyFormatter(value)
}

// ── Component ─────────────────────────────────────────────────────────────────

interface TradeRateSummaryViewProps {
  selectedYear: number
  className?: string
}

export function TradeRateSummaryView({ selectedYear, className }: TradeRateSummaryViewProps) {
  const [data, setData] = useState<TradeRateSummaryResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [whatIfMode, setWhatIfMode] = useState(false)
  const [excluded, setExcluded] = useState<Set<string>>(new Set())
  const [expandedParty, setExpandedParty] = useState<string | null>(null)

  useEffect(() => {
    async function load() {
      setLoading(true)
      setError(null)
      const res = await api_fetch<TradeRateSummaryResponse>(`/promotions/trade-rate-summary`, {
        params: { year: selectedYear },
      })
      if (res.ok) {
        setData(res.value.data)
      } else {
        setError("Failed to load trade rate data")
      }
      setLoading(false)
    }
    load()
    setExcluded(new Set())
  }, [selectedYear])

  const promotions_by_party = useMemo(() => {
    const map = new Map<string, TradeRatePromotion[]>()
    for (const promo of data?.promotions ?? []) {
      const list = map.get(promo.party_key) ?? []
      list.push(promo)
      map.set(promo.party_key, list)
    }
    for (const list of map.values()) {
      list.sort((a, b) => b.planned_amount - a.planned_amount)
    }
    return map
  }, [data])

  const adjusted_rows = useMemo(() => {
    if (!data) return []
    if (!whatIfMode || excluded.size === 0) return data.rows
    return data.rows.map((row) => apply_exclusions(row, data.promotions, excluded))
  }, [data, whatIfMode, excluded])

  const adjusted_totals = useMemo(() => {
    if (!data) return null
    if (!whatIfMode || excluded.size === 0) return data.totals
    return recompute_totals(adjusted_rows, data.totals)
  }, [data, adjusted_rows, whatIfMode, excluded])

  if (loading) {
    return (
      <div class="flex items-center justify-center py-20 text-gray-400">
        <Loader2 className="h-6 w-6 animate-spin mr-2" />
        Loading trade rate data…
      </div>
    )
  }
  if (error || !data || !adjusted_totals) {
    return <div class="py-10 text-center text-red-500 text-sm">{error ?? "No data"}</div>
  }

  const baseline_planned_rate = data.totals.planned_trade_rate_pct
  const whatif_delta =
    whatIfMode && excluded.size > 0 && baseline_planned_rate !== null && adjusted_totals.planned_trade_rate_pct !== null
      ? adjusted_totals.planned_trade_rate_pct - baseline_planned_rate
      : null

  function toggle_excluded(promotion_id: string) {
    setExcluded((prev) => {
      const next = new Set(prev)
      next.has(promotion_id) ? next.delete(promotion_id) : next.add(promotion_id)
      return next
    })
  }

  return (
    <div class={className}>
      {/* Header stats */}
      <div class="grid grid-cols-5 gap-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
        <HeaderStat label="Revenue" value={money(adjusted_totals.revenue)} />
        <HeaderStat label="Planned Spend" value={money(adjusted_totals.planned_amount)} sub={`${adjusted_totals.promotion_count} promotions`} />
        <HeaderStat label="Actual Spend" value={money(adjusted_totals.total_spend)} sub={`${money(adjusted_totals.unplanned_spend)} unplanned`} />
        <HeaderStat
          label="Planned Trade Rate"
          value={pct(adjusted_totals.planned_trade_rate_pct)}
          sub={whatif_delta !== null ? `${whatif_delta > 0 ? "+" : ""}${whatif_delta.toFixed(2)} pts vs baseline` : undefined}
          valueClass={whatif_delta !== null ? (whatif_delta < 0 ? "text-emerald-600" : "text-red-600") : "text-gray-900"}
        />
        <HeaderStat label="Actual Trade Rate" value={pct(adjusted_totals.actual_trade_rate_pct)} />
      </div>

      {/* What-if toggle */}
      <div class="mt-3 flex items-center gap-3">
        <Button variant={whatIfMode ? "default" : "outline"} size="sm" onClick={() => setWhatIfMode((m) => !m)}>
          <FlaskConical className="h-4 w-4 mr-1.5" />
          What-if
        </Button>
        {whatIfMode && (
          <p class="text-xs text-gray-500">
            Expand a customer and untick promotions to see what removing them does to the trade rate
            {excluded.size > 0 ? ` — ${excluded.size} excluded` : ""}.
          </p>
        )}
        {whatIfMode && excluded.size > 0 && (
          <button onClick={() => setExcluded(new Set())} class="text-xs text-gray-400 hover:text-gray-600 underline">
            Reset
          </button>
        )}
      </div>

      {/* Table */}
      <div class="mt-4 rounded-lg border border-gray-200 overflow-x-auto">
        <table class="w-full text-left border-collapse">
          <thead>
            <tr class="bg-gray-50 border-b border-gray-200 text-xs text-gray-500">
              <th class="px-3 py-2 font-medium">Customer</th>
              <th class="px-3 py-2 font-medium text-right">Revenue</th>
              <th class="px-3 py-2 font-medium text-right">Planned $</th>
              <th class="px-3 py-2 font-medium text-right">Latest Est. $</th>
              <th class="px-3 py-2 font-medium text-right">Matched $</th>
              <th class="px-3 py-2 font-medium text-right">Unplanned $</th>
              <th class="px-3 py-2 font-medium text-right">Plan Rate</th>
              <th class="px-3 py-2 font-medium text-right">Actual Rate</th>
            </tr>
          </thead>
          <tbody>
            {adjusted_rows.map((row) => {
              const party_promotions = promotions_by_party.get(row.party_key) ?? []
              const expanded = expandedParty === row.party_key
              const is_unplanned_account = row.promotion_count === 0 && row.total_spend !== 0
              return (
                <>
                  <tr
                    key={row.party_key}
                    class="border-b border-gray-100 hover:bg-blue-50 cursor-pointer transition-colors"
                    onClick={() => setExpandedParty(expanded ? null : row.party_key)}
                  >
                    <td class="px-3 py-2.5 text-sm text-gray-800">
                      <span class="inline-flex items-center gap-1.5">
                        {party_promotions.length > 0 ? (
                          expanded ? <ChevronDown className="h-3.5 w-3.5 text-gray-400" /> : <ChevronRight className="h-3.5 w-3.5 text-gray-400" />
                        ) : (
                          <span class="w-3.5" />
                        )}
                        <span class="font-medium">{row.party_label}</span>
                        {is_unplanned_account && (
                          <span class="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-amber-50 text-amber-700">
                            Unplanned account
                          </span>
                        )}
                      </span>
                    </td>
                    <td class="px-3 py-2.5 text-sm text-right text-gray-600">{money(row.revenue)}</td>
                    <td class="px-3 py-2.5 text-sm text-right font-medium text-gray-800">{money(row.planned_amount)}</td>
                    <td class="px-3 py-2.5 text-sm text-right text-gray-600">{money(row.latest_estimate_amount)}</td>
                    <td class="px-3 py-2.5 text-sm text-right text-gray-600">{money(row.matched_spend)}</td>
                    <td class={`px-3 py-2.5 text-sm text-right ${row.unplanned_spend > 0 ? "text-amber-700" : "text-gray-400"}`}>
                      {money(row.unplanned_spend)}
                    </td>
                    <td class="px-3 py-2.5 text-sm text-right text-gray-700">{pct(row.planned_trade_rate_pct)}</td>
                    <td class="px-3 py-2.5 text-sm text-right font-medium text-gray-800">{pct(row.actual_trade_rate_pct)}</td>
                  </tr>
                  {expanded &&
                    party_promotions.map((promo) => (
                      <tr key={promo.id} class="border-b border-gray-50 bg-gray-50/50 text-xs">
                        <td class="pl-10 pr-3 py-1.5 text-gray-600">
                          <span class="inline-flex items-center gap-2">
                            {whatIfMode && (
                              <Checkbox
                                checked={!excluded.has(promo.id)}
                                onCheckedChange={() => toggle_excluded(promo.id)}
                              />
                            )}
                            <span class={excluded.has(promo.id) ? "line-through text-gray-400" : ""}>
                              {promo.remote_promo_id ? `${promo.remote_promo_id} · ` : ""}
                              {promo.name}
                              {promo.promotion_type ? ` (${promo.promotion_type})` : ""}
                            </span>
                          </span>
                        </td>
                        <td />
                        <td class={`px-3 py-1.5 text-right ${excluded.has(promo.id) ? "line-through text-gray-400" : "text-gray-600"}`}>
                          {money(promo.planned_amount)}
                        </td>
                        <td class={`px-3 py-1.5 text-right ${excluded.has(promo.id) ? "line-through text-gray-400" : "text-gray-500"}`}>
                          {money(promo.latest_estimate_amount)}
                        </td>
                        <td class="px-3 py-1.5 text-right text-gray-500">{money(promo.actual_amount)}</td>
                        <td colspan={3} />
                      </tr>
                    ))}
                </>
              )
            })}
          </tbody>
          <tfoot>
            <tr class="bg-gray-50 border-t border-gray-200 text-sm font-semibold text-gray-900">
              <td class="px-3 py-2.5">Total</td>
              <td class="px-3 py-2.5 text-right">{money(adjusted_totals.revenue)}</td>
              <td class="px-3 py-2.5 text-right">{money(adjusted_totals.planned_amount)}</td>
              <td class="px-3 py-2.5 text-right">{money(adjusted_totals.latest_estimate_amount)}</td>
              <td class="px-3 py-2.5 text-right">{money(adjusted_totals.matched_spend)}</td>
              <td class="px-3 py-2.5 text-right">{money(adjusted_totals.unplanned_spend)}</td>
              <td class="px-3 py-2.5 text-right">{pct(adjusted_totals.planned_trade_rate_pct)}</td>
              <td class="px-3 py-2.5 text-right">{pct(adjusted_totals.actual_trade_rate_pct)}</td>
            </tr>
          </tfoot>
        </table>
      </div>

      <p class="mt-2 text-xs text-gray-400">
        Revenue and actual spend come from {selectedYear} invoices and deductions; planned and latest-estimate
        spend from the promotion plan. “Unplanned” is deduction spend with no promotion matched to it.
        Rates compare planned spend against <em>actual</em> revenue — until forecast revenue lands here,
        a partially billed year understates revenue and overstates the planned rate.
      </p>
    </div>
  )
}

interface HeaderStatProps {
  label: string
  value: string
  sub?: string
  valueClass?: string
}

function HeaderStat({ label, value, sub, valueClass = "text-gray-900" }: HeaderStatProps) {
  return (
    <div>
      <p class="text-xs text-gray-500 mb-0.5">{label}</p>
      <p class={`text-lg font-semibold ${valueClass}`}>{value}</p>
      {sub && <p class="text-xs text-gray-400">{sub}</p>}
    </div>
  )
}
